import { DatabaseFieldType, ImportedFileType } from '@databases/types/database';
import { DatabaseFilterType } from '@databases/types/database/view';

import { TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH } from '../../constants';
import { cheerioIterator } from '../../lib/dom';
import { getImageUrl } from '../../media-client';
import { encodeHtml, generateId, isFile } from '../../utils';
import { FieldType } from '../field-types';

const attachmentFieldType: FieldType<DatabaseFieldType.ATTACHMENT> = {
  supportedFilterTypes: [DatabaseFilterType.EMPTY],

  supportedImportTypes: [ImportedFileType.HTML],

  renderExportView: async (
    { field, value },
    state,
    renderContext,
    recursionCount,
    contentId
  ) => {
    if (!Array.isArray(value) || !value.length) {
      return '';
    }

    return (
      await Promise.all(
        value.map(async (media) => {
          if ('fileId' in media && !!media.fileId) {
            const imageUrl = await getImageUrl(state, contentId, media.fileId);
            return imageUrl
              ? `<span class="confluence-embedded-file-wrapper"><img class="confluence-embedded-image" src="${encodeHtml(
                  `${imageUrl}`
                )}" width="${encodeHtml(
                  field.width ?? TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH
                )}" data-image-id="${media.fileId}"/></span>`
              : imageUrl;
          } else {
            return '';
          }
        })
      )
    ).join('<br/>');
  },

  parseFromHtml: (el) => {
    return [...cheerioIterator(el.find('img[src]'))].flatMap((a) => {
      return [
        { src: a.attr('src') ?? '', fileId: a.data('image-id') ?? '', _id: generateId() },
      ];
    });
  },

  getInitialValue: () => [],

  isEmptyValue: ({ value }) => !value || !value.length,

  isEqualValue: ({ value: value1 }, { value: value2 }) => {
    if (!value1 || !value2 || value1.length != value2.length) {
      return false;
    }

    for (let i = 0; i < value1.length; i++) {
      const val1 = value1[i];
      const val2 = value2[i];
      if ('src' in val1 && 'src' in val2 && val1.src !== val2.src) {
        return false;
      } else if ('fileId' in val1 && 'fileId' in val2 && val1.fileId !== val2.fileId) {
        return false;
      }
    }
    return true;
  },

  getStringRepresentation: ({ value }): string => {
    if (value) {
      if (isFile(value)) {
        return value.name;
      } else if ('fileId' in value) {
        return value.fileId as string;
      } else if (Array.isArray(value) && value.length) {
        return value.map((file) => ('fileId' in file ? file.fileId : '')).join('\n');
      }
    }

    return '';
  },

  noDefaultValue: true,
};

export default attachmentFieldType;
